import { defaultLanguage, languagesCodesListFull } from 'constants/languages';

import { ILanguagesCodes } from '@picsart/rc/languages';

import { PageType } from 'constants/pages';

import trim from 'utils/trim';
import fetchWithFallback from 'services/CMSFetcher/fetchWithFallback';
import getBannerBlockCarouselMapper from './componentMappings/bannerBlockCarouselMapper';
import getBannerBlockMapper from './componentMappings/bannerBlockMapper';
import getPageBySlug from './getPageBySlug';
import getButtonPropsMapper from './componentMappings/mapButtonProps';
import getIconBlockWithCtasMapper from './componentMappings/iconBlockWithCtasMapper';
import getBannerWithImageMapper from './componentMappings/bannerWithImageMapper';
import getBodyTextWithDoubleCTAMapper from './componentMappings/bodyTextWithDoubleCTAMapper';
import getBodyTextsContainerMapper from './componentMappings/bodyTextsContainerMapper';
import getHeaderBannerWithDynamicSliderMapper from './componentMappings/headerBannerWithDynamicSliderMapper';
import getHowToMapper from './componentMappings/howToMapper';
import getImageSliderWithCTAMapper from './componentMappings/imageSliderWithCTAMapper';
import getImageSliderWithThumbnailCarouselMapper from './componentMappings/imageSliderWithThumbnailCarouselMapper';
import getInThePressMapper from './componentMappings/inThePressMapper';
import getListWithPaginationAndCTAMapper from './componentMappings/listWithPaginationAndCTAMapper';
import getUseCasesMapper from './componentMappings/useCasesMapper';
import getValueItemisationMapper from './componentMappings/valueItemisationMapper';

import componentNameCmsToJsonMap from './componentNameMap';
import { AppRoutes, JsonResponseModel, StrapiDataModel, StrapiResponseModel } from './types';

// component real name: mapper function
const componentContentCustomMapper = {
  BannerWithImage: getBannerWithImageMapper,
  ComparisonTable: getButtonPropsMapper,
  BodyTextWithCTA: getButtonPropsMapper,
  BodyTextWithMarketingProperties: getButtonPropsMapper,
  ImageAndCTA: getButtonPropsMapper,
  AnimatedBannerWithBullets: getButtonPropsMapper,
  StickyBar: getButtonPropsMapper,
  HeaderBannerWithCTAAndBullets: getButtonPropsMapper,
  BannerBlockCarousel: getBannerBlockCarouselMapper,
  BannerWithWallpaper: getButtonPropsMapper,
  HowToIcons: getButtonPropsMapper,
  IntroText: getButtonPropsMapper,
  IconBlockWithCTAs: getIconBlockWithCtasMapper,
  ListWithPaginationAndCTA: getListWithPaginationAndCTAMapper,
  HeaderBannerWithDynamicSlider: getHeaderBannerWithDynamicSliderMapper,
  ImageSliderWithCTA: getImageSliderWithCTAMapper,
  BodyTextWithDoubleCTA: getBodyTextWithDoubleCTAMapper,
  BodyTextsContainer: getBodyTextsContainerMapper,
  ImageSliderWithThumbnailCarousel: getImageSliderWithThumbnailCarouselMapper,
  // redesigned components
  HowTo: getHowToMapper,
  BannerBlock: getBannerBlockMapper,
  UseCases: getUseCasesMapper,
  ValueItemisation: getValueItemisationMapper,
  InThePress: getInThePressMapper,
};

// get custom mapper or using result content
function getComponentMappedContent(componentRealName: string, content: any): any {
  const contentCustomMapper = componentContentCustomMapper[componentRealName];

  if (contentCustomMapper) {
    const mappedContent = contentCustomMapper(content);

    if (mappedContent) {
      return mappedContent;
    }
  }
  return content;
}

function addCmsNameAndId(obj: object, parentCmsName: string) {
  const newObj = { ...obj }; // Create a shallow copy of 'obj'
  Object.keys(newObj).forEach(key => {
    if (newObj[key] && typeof newObj[key] === 'object') {
      newObj[key].cmsName = `${parentCmsName}/${key}`;
      newObj[key].cmsId = newObj[key].id || 0;
      addCmsNameAndId(newObj[key], newObj[key]?.cmsName ?? '');
    }
  });
  return newObj;
}

// get component mapped name or use name standard converter (simple_component_name => SimpleComponentName)
function getComponentMappedName(componentName: string): string {
  let componentMappedName = componentNameCmsToJsonMap[componentName];
  componentMappedName =
    componentMappedName ??
    componentName
      .split('.')
      .pop()
      .split('-')
      .map(item => `${item[0].toUpperCase()}${item.substring(1)}`)
      .join('');

  return componentMappedName;
}

function componentsMapper(componentsData: Array<any>): Array<any> {
  return componentsData.map(componentData => {
    const { __component, ...content } = componentData;
    const componentRealName = getComponentMappedName(__component);
    let mappedContent = getComponentMappedContent(componentRealName, content);
    mappedContent.cmsName = __component ?? '';
    mappedContent = addCmsNameAndId(mappedContent, mappedContent.cmsName);
    mappedContent.cmsId = mappedContent.componentId || mappedContent.id;

    return {
      component: componentRealName,
      content: mappedContent,
    };
  });
}

function checkDefaultLanguage(slug: string): string {
  if (slug.split('/')[0] === defaultLanguage) {
    return slug.substring(`${defaultLanguage}/`.length);
  }
  return slug;
}

export function mappingPageData(
  pageData: StrapiResponseModel,
  settings?: StrapiDataModel,
): JsonResponseModel {
  try {
    const settingsValue = settings || {};
    if (pageData?.data) {
      const mergedData = { ...pageData.data, ...settingsValue };
      const { Components, ...rest } = mergedData;

      const mappedData = {
        ...rest,
        components: componentsMapper(mergedData.Components),
      };
      return mappedData;
    }
    return {
      ...pageData,
      ...settingsValue,
    } as unknown as JsonResponseModel;
  } catch (e) {
    console.error(e);
    throw new Error(e);
  }
}

export default async function getPageData({
  pageSlug,
  locale = defaultLanguage,
  queryParam,
  isDraft,
  pageType,
}: {
  pageSlug: string;
  locale?: string;
  queryParam?: Record<string, string>;
  isDraft?: boolean;
  pageType?: PageType;
}): Promise<StrapiResponseModel> {
  let slug = `${
    locale === 'en' || trim(AppRoutes.DRAFT, '/') === pageSlug ? '' : `${locale}/`
  }${pageSlug}`;

  try {
    if (pageSlug === AppRoutes.HOME || languagesCodesListFull.includes(slug as ILanguagesCodes)) {
      slug = `${locale === defaultLanguage ? '' : `${locale}`}`;
    }

    if (pageSlug.split('/')[0] === AppRoutes.BRANDS || slug === AppRoutes.APPS) {
      const apiPath = locale === 'zh_HANT' ? 'translations/zh-Hant' : `translations/${locale}`;
      // TODO: remove this after migration brands to the CMS
      const [pageContent, translations] = await Promise.all([
        fetch(`${process.env.PUBLIC_URL}/data/landings/${pageSlug}.json`).then(r => r.json()),
        fetchWithFallback(apiPath),
      ]);

      const pageData = {
        ...pageContent,
        name: pageContent.seoSettings.ogTitle || pageContent.seoSettings.title,
        translations,
      };

      return pageData as StrapiResponseModel;
    }

    const response = await getPageBySlug({
      slug: checkDefaultLanguage(slug),
      isDraft,
      queryParam,
      pageType,
    });

    if (pageType === PageType.LandingOrHub && !response?.data?.Components) {
      throw new Error("Can't fetch data from the CMS");
    }

    return response;
  } catch (e) {
    throw new Error(e);
  }
}
